/**
 * Where the SERVER is, when the page itself does not live on it (ADR-017).
 *
 * The native shells load the UI from inside the binary — that is the whole
 * point: a server that can hand the client its crypto code owns the client
 * (SEC-01). They inject `window.__VOXIOX_SERVER_URL__` before any module
 * runs. In the browser the page really is on the server, the global is
 * absent, and everything here degrades to the same-origin behaviour the
 * code always had.
 *
 * One module answers the question so it cannot be answered differently in
 * nine places — which is how the last attempt at embedding died: API,
 * WebSocket and SIP each asked `location` and each got the wrong answer.
 */

export function serverOrigin() {
  if (typeof window === 'undefined') return '';
  const raw = window.__VOXIOX_SERVER_URL__ || '';
  return raw ? String(raw).replace(/\/+$/, '') : '';
}

/** True inside a native shell that loads the UI from the binary. */
export function isEmbeddedShell() {
  return serverOrigin() !== '';
}

/** host (with port, if any) of the server — for WebSocket URLs. */
export function serverHost() {
  const o = serverOrigin();
  if (!o) return typeof window === 'undefined' ? '' : window.location.host;
  try { return new URL(o).host; } catch (e) { return window.location.host; }
}

/** hostname (no port) of the server — for SIP domain and STUN. */
export function serverHostname() {
  const o = serverOrigin();
  if (!o) return typeof window === 'undefined' ? '' : window.location.hostname;
  try { return new URL(o).hostname; } catch (e) { return window.location.hostname; }
}

/**
 * The origin to put into links a PERSON will open — join links, add-contact,
 * "войти". Always the server: the recipient opens them in a browser, and
 * the shell's own origin (file://, appassets) means nothing outside it.
 */
export function publicOrigin() {
  return serverOrigin() || (typeof window === 'undefined' ? '' : window.location.origin);
}

/** wss:// (or ws:// for a plain-http server) base for server WebSockets. */
export function serverWsBase() {
  const o = serverOrigin();
  if (o) return o.replace(/^http/, 'ws');
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

/**
 * Where SIP's WebSocket lives, in the order to try (SEC-ARCH-007).
 *
 * `/sip-ws` on the ordinary 443 first: same host, same port, same pin as the
 * API, so it takes whatever path the local proxy chose — a relay included.
 * Asterisk's own port 8089 second: it can only ever be reached directly (a
 * relay passes bytes by SNI to ONE address), so while it was the only URL,
 * SIP was the one thing still naming CORE while everything else went through
 * the relay. Keeping it as the fallback is the rollback: a server whose nginx
 * does not know `/sip-ws` yet still answers on 8089.
 */
export function sipWsUrls(host = serverHostname(), secure = true) {
  const scheme = secure ? 'wss' : 'ws';
  return [`${scheme}://${host}/sip-ws`, `${scheme}://${host}:8089/ws`];
}
